'use client';

import { forwardRef, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { CALENDAR_DAYS } from './constants/calendar-days';
import { CalendarRootContext } from './context/calendar-root.context';
import { useCalendarBodyRenderProps } from './hooks/use-calendar-renders';
import {
  calendarBodyGridRowStyles,
  calendarBodyGridStyles,
  calendarBodyHeaderStyles,
  calendarBodyStyles,
} from './styles/calendar-body.styles';
import type { CalendarBodyPropsInterface } from './types/calendar-body.types';

export const CalendarBody = forwardRef<HTMLDivElement, CalendarBodyPropsInterface>(
  (props: CalendarBodyPropsInterface, forwardedRef) => {
    const { calendar } = useContext(CalendarRootContext);

    const { renderDay, renderDayOfWeek, ...nonRenderProps } = useCalendarBodyRenderProps(props);

    const {
      className,
      'css': cssProp,
      'aria-labelledby': ariaLabelledBy = calendar?.headerId,
      ...rest
    } = nonRenderProps;

    const headerStyles = cx('c11n', css(calendarBodyHeaderStyles));
    const gridStyles = cx('c11n', css(calendarBodyGridStyles));
    const gridRowStyles = cx('c11n', css(calendarBodyGridRowStyles));
    const styles = cx('c11n', css(calendarBodyStyles, cssProp), className);

    const daysOfTheWeek = useMemo(
      () => (
        <div role="rowgroup" className="c11n">
          <div role="row" className={headerStyles}>
            {CALENDAR_DAYS.map((day) => (
              <span key={`day-of-week-${day}`} className="c11n">
                {renderDayOfWeek({ day })}
              </span>
            ))}
          </div>
        </div>
      ),
      [renderDayOfWeek, headerStyles],
    );

    const days = useMemo(
      () => (
        <div role="rowgroup" className={gridStyles}>
          {calendar?.monthMatrix?.map((week, weekIndex) => (
            <div role="row" key={`week-${weekIndex}`} className={gridRowStyles}>
              {week.map((day, dayIndex) => {
                if (day === null) {
                  return <div role="gridcell" key={`${weekIndex}_${dayIndex}`} className="c11n" />;
                }

                return (
                  <div role="gridcell" key={`${weekIndex}_${dayIndex}`} className="c11n">
                    {renderDay(calendar?.getDayProps(day))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ),
      [calendar, renderDay, gridRowStyles, gridStyles],
    );

    return (
      <div
        ref={forwardedRef}
        role="grid"
        aria-labelledby={ariaLabelledBy}
        data-c11n-component="Calendar.Body"
        data-c11n-version={__C11N_VERSION__}
        className={styles}
        {...rest}
      >
        {daysOfTheWeek}
        {days}
      </div>
    );
  },
);

CalendarBody.displayName = 'Calendar.Body';
